/* eslint-disable */
import { Button, Form, Input, message, Select, Space, Spin, TreeSelect } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SnippetsOutlined } from "@ant-design/icons";
import "@/common.css";
import "./component/Details.css"; 
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import UploadFile from "@/components/UplaodFileMaintenance";
import JobSteps, { getAccountId, isOk } from "./component/JobSteps";

const COMMON_AREA = "CommonArea";
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];

/* ---------- 工具 ---------- */
// 防抖:远程搜索不要每敲一个字就发请求
const useDebounced = (fn, delay = 300) => {
  const timer = useRef(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => () => clearTimeout(timer.current), []);
  return useCallback(
    (...args) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay],
  );
};

// 远程搜索会替换 options,已选项若不在新列表里,Select 会显示成裸 id,所以补回去
const withSelected = (options, selected) =>
  selected && !options.some((o) => o.value === selected.value)
    ? [selected, ...options]
    : options;

const formatJobTree = (data = []) =>
  data.map((item) => ({
    title: item.name,
    value: Number(item.id),
    key: Number(item.id),
    disableCheckbox: !!item.children, // 有子节点的只能勾子节点
    children: item.children ? formatJobTree(item.children) : undefined,
  }));

/* ---------- 新建工单表单(第 1 步) ---------- */
function CreateJobForm({ onCreated }) {
  const [form] = Form.useForm();
  const uploadRef = useRef();

  const [propOptions, setPropOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [requestByOptions, setRequestByOptions] = useState([]);
  const [jobTree, setJobTree] = useState([]);
  const [selectedProp, setSelectedProp] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  /* 数据获取 */
  const fetchProps = async (content = "") => {
    try {
      const res = await http.get(
        `${api.getPropSearch}?content=${encodeURIComponent(content)}`,
      );
      setPropOptions(
        (res?.data || []).map((i) => ({
          value: i.propId,
          label: i.propName,
          city: i.city,
          add: i.street,
          prop: i.prop,
        })),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUnits = async (propId, content = "") => {
    if (!propId) return;
    try {
      const res = await http.get(
        `${api.unitSearch}?content=${encodeURIComponent(content)}&propId=${propId}`,
      );
      setUnitOptions([
        { value: COMMON_AREA, label: "Common Area" },
        ...(res?.data || []).map((i) => ({
          value: i.unitId,
          label: i.unitName,
          city: i.city,
          add: i.street,
          prop: i.prop,
          unit: i.unit,
        })),
      ]);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRequestBy = async (unitId) => {
    try {
      const res = await http.get(`${api.requestByType}?content=&unitId=${unitId}`);
      setRequestByOptions(
        (res?.data || []).map((i) => ({
          value: `${i.createByType}-${i.requestByName}`,
          label: i.requestByName,
          originalData: i,
        })),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const fetchJobTree = async () => {
    try {
      const res = await http.get(api.getjobTypeshow);
      setJobTree(formatJobTree(res?.data));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchProps("");
    fetchJobTree(); // 只需加载一次(原来每次选 Unit 都重复请求)
  }, []);

  const searchProps = useDebounced((v) => fetchProps(v));
  const searchUnits = useDebounced((v) =>
    fetchUnits(form.getFieldValue("propId"), v),
  );

  /* 级联选择:上级变化时清空下级,避免残留旧数据 */
  const handlePropChange = (value, option) => {
    setSelectedProp(option || null);
    form.setFieldsValue({ unitId: undefined, requestBy: undefined });
    setSelectedUnit(null);
    setUnitOptions([]);
    setRequestByOptions([]);
    if (value) fetchUnits(value, "");
  };

  const handleUnitChange = (value, option) => {
    setSelectedUnit(option || null);
    form.setFieldsValue({ requestBy: undefined });
    setRequestByOptions([]);
    if (value) fetchRequestBy(value === COMMON_AREA ? "-1" : value);
  };

  /* 上传 */
  const uploadFiles = (files) =>
    Promise.all(
      files.map(async (f) => {
        const fd = new FormData();
        fd.append("files", f.originFileObj);
        const res = await http.post(api.UploadFile, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!isOk(res)) throw new Error(res?.msg || "upload failed");
        return res.data[0];
      }),
    );

  const handleUpload = async (files) => {
    const invalid = files.filter((f) => !ALLOWED_IMAGE_TYPES.includes(f.type));
    if (invalid.length > 0) {
      message.error(
        `The following files are not JPG or PNG: ${invalid
          .map((f) => `"${f.name}"`)
          .join(", ")}`,
      );
      uploadRef.current?.clearFiles();
      return;
    }
    try {
      const uploaded = await uploadFiles(files);
      setFileList(uploaded); // 包括清空的情况
      form.setFieldsValue({ files: uploaded });
    } catch (e) {
      console.error(e);
      message.error("File upload failed.");
    }
  };

  /* 提交(CommonArea 与普通 Unit 两个分支合并) */
  const handleFinish = async (values) => {
    const requestBy = requestByOptions.find((o) => o.value === values.requestBy);
    if (!requestBy) return;

    const isCommon = values.unitId === COMMON_AREA;
    const src = isCommon ? selectedProp : selectedUnit; // 地址信息来源
    const info = requestBy.originalData;

    const payload = {
      createBy: getAccountId(),
      createByType: info.createByType,
      propId: values.propId,
      unitId: isCommon ? "-1" : values.unitId,
      jobType: String(values.jobType),
      remark: values.remark.trim(),
      filePaths: fileList,
      propUnit: isCommon
        ? `${selectedProp?.prop} - Common Area`
        : `${selectedUnit?.prop} - ${selectedUnit?.unit}`,
      city: src?.city,
      address: src?.add,
      requestByName: info.requestByName,
    };

    setSubmitting(true);
    try {
      const res = await http.post(api.MaintenanceAdd, payload);
      if (isOk(res)) {
        message.success("Successfully Added");
        onCreated(res.data);
      } else {
        commonFn.modalFailedFn(res);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      labelCol={{ xs: 24, sm: 4 }}
      wrapperCol={{ xs: 24, sm: 18 }}
      validateMessages={{ required: "${label} is required!" }}
    >
      <Form.Item name="propId" label="Property" rules={[{ required: true }]}>
        <Select
          showSearch
          allowClear
          placeholder="Select a prop"
          filterOption={false}
          options={withSelected(propOptions, selectedProp)}
          onChange={handlePropChange}
          onSearch={searchProps}
        />
      </Form.Item>

      <Form.Item name="unitId" label="Unit" rules={[{ required: true }]}>
        <Select
          showSearch
          placeholder="Select a unit"
          filterOption={false}
          options={withSelected(unitOptions, selectedUnit)}
          onChange={handleUnitChange}
          onSearch={searchUnits}
        />
      </Form.Item>

      <Form.Item name="jobType" label="Job" rules={[{ required: true }]}>
        <TreeSelect
          treeData={jobTree}
          placeholder="Select a job type"
          treeCheckable
          multiple
          allowClear
          showSearch
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          treeNodeFilterProp="title"
          style={{ width: "100%" }}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
        />
      </Form.Item>

      {/* TextArea 直接放在 Form.Item 里,字数统计用 showCount,不再手动维护 3 个 state */}
      <Form.Item
        name="remark"
        label="Remark"
        rules={[
          { required: true, whitespace: true },
          { min: 5, message: "Remark requires at least 5 characters." },
        ]}
      >
        <Input.TextArea
          rows={3}
          maxLength={250}
          showCount
          placeholder="Input at least 5 characters (max 250)"
        />
      </Form.Item>

      <Form.Item name="requestBy" label="Request By" rules={[{ required: true }]}>
        <Select placeholder="Select request by" options={requestByOptions} />
      </Form.Item>

      <Form.Item name="files" label="Files">
        <UploadFile ref={uploadRef} sendDataToParent={handleUpload} />
        <div className="uploadText">Uploaded files only support JPG or PNG.</div>
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }} className="btnCenter">
        <Space align="center" size="large">
          <Button type="primary" htmlType="submit" loading={submitting} size="large">
            Submit
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

/* ---------- 页面 ---------- */
export default function NewOrder() {
  const location = useLocation();
  const state = location.state || {};

  const [loading, setLoading] = useState(!!state.id);
  const [maintenanceId, setMaintenanceId] = useState("");
  const [processData, setProcessData] = useState(null);
  const [maintenance, setMaintenance] = useState(null);

  const fetchProcess = async (id) => {
    const res = await http.get(`${api.process}?id=${id}`);
    return res?.data || null;
  };

  const refreshProcess = async () => {
    try {
      setProcessData(await fetchProcess(maintenanceId));
    } catch (e) {
      console.error(e);
    }
  };

  // known:从列表页带过来的工单详情,有就不用再请求
  const loadJob = async (id, known) => {
    setMaintenanceId(id);
    setLoading(true);
    try {
      const [proc, detail] = await Promise.all([
        fetchProcess(id),
        known ? null : http.get(`${api.getIdDetails}?id=${id}`),
      ]);
      setProcessData(proc);
      setMaintenance(known || detail?.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state.id) loadJob(state.id, state);
  }, []);

  return (
    <div className="orderMain">
      <div className="page_title">
        <span>
          <SnippetsOutlined style={{ marginRight: 8 }} />
          New Job
        </span>
      </div>

      <div className="stepMain">
        {loading ? (
          <div className="page_center">
            <Spin size="large" />
          </div>
        ) : (
          <JobSteps
            maintenanceId={maintenanceId}
            processData={processData}
            maintenance={maintenance}
            onRefresh={refreshProcess}
            // 还没创建工单时,第 1 步放新建表单;创建后用默认的详情展示
            requestContent={
              maintenanceId ? undefined : (
                <CreateJobForm onCreated={(id) => loadJob(id)} />
              )
            }
          />
        )}
      </div>
    </div>
  );
}
